import type {
    ComposableOptionsBase,
    MenuRequest,
    GetMenusResponse,
    DrawerMenuNames,
    DrawerMenu
} from '~/types';
import {
    useCustomFetch
} from '~/composables';
import { useApiUtils } from 'x1-base/composables';
import { isUndefined as lo_isUndefined } from 'es-toolkit';
import type { NitroFetchOptions } from 'nitropack';
import type { NuxtError } from '#app';

export const drawerMenusPrimary: DrawerMenuNames[] = ['main', 'user', 'cart', 'search'];

// Drawer state is per-request on the server (useState) — a module-scope
// reactive would be shared by every SSR request for the process lifetime.
// The unwrapped `.value` is the reactive object, so consumers keep the same
// `drawerMenus.main = false` usage.
const DrawerMenus = (): DrawerMenu => useState<DrawerMenu>('keyDrawerMenus', () => ({
    main: false,
    user: false,
    cart: false,
    search: false
})).value;

export function useDrawerMenus() {
    const drawerMenus = DrawerMenus();
    const isOpenDrawerMenu = computed<boolean>(() => Object.values(drawerMenus).some((s) => s));
    const openedDrawerMenu = computed<Nilish<DrawerMenuNames>>(() => drawerMenusPrimary.filter((name) => !!drawerMenus[name] && name)[0]);

    return {
        drawerMenus,
        isOpenDrawerMenu,
        openedDrawerMenu,
        toggleDrawerMenu
    };
}

export function toggleDrawerMenu(
    menuName?: Nilish<DrawerMenuNames>,
    value?: boolean
): void {
    const drawerMenus = DrawerMenus();
    const v: boolean = lo_isUndefined(value) ?
        menuName ? !drawerMenus[menuName] : false
        : value;

    drawerMenusPrimary.forEach((name) => {
        drawerMenus[name] = menuName === name ? v : false;
    });
}

export interface UseMenusOptions extends ComposableOptionsBase {}
export function useMenus(options: Partial<UseMenusOptions> = {}) {
    const apiBasePath = `/v1/menus`;
    const $_fetch = useCustomFetch({ siteContext: options.siteContext });
    const useApiUtilsObj = useApiUtils();
    const drawerMenus = DrawerMenus();

    async function getMenus(): Promise<GetMenusResponse | NuxtError> {
        const url = `${apiBasePath}/`;
        let response: MenuRequest | void;

        const config: NitroFetchOptions<string> = {
            method: 'GET'
        };

        try {
            response = await $_fetch<MenuRequest>(url, config);

            return {
                menuMain: response?.header
                // menuMobile: response?.sidebar,
                // menuFooter: response?.footer
            };
        } catch (err) {
            return useApiUtilsObj.getNuxtErrorObj(err);
        }
    }

    function resetMenuState(): void {
        drawerMenusPrimary.forEach((name) => {
            drawerMenus[name] = false;
        });
    }

    return {
        getMenus,
        drawerMenus,
        resetMenuState
    };
}
